import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ReceiptText, Sparkles, Wallet } from "lucide-react";
import { useI18n } from "../../i18n/LanguageProvider";
import { Container, SectionLabel } from "../Container";
import { Reveal } from "../Reveal";
import { StripeMark } from "../StripeMark";

/**
 * Global fee config. Stripe's real fee varies by country & payment method;
 * this is only an EU-card example and can be changed here in one place.
 */
export const STRIPE_FEE = { percent: 1.5, fixed: 0.25 };
export const CENTAUROS_PERCENT = 1;

export function Pricing() {
  const { t, locale } = useI18n();
  const [amount, setAmount] = useState(100);

  const a = Number.isFinite(amount) ? Math.max(0, amount) : 0;
  const stripeFee = a > 0 ? a * (STRIPE_FEE.percent / 100) + STRIPE_FEE.fixed : 0;
  const centaurosFee = a * (CENTAUROS_PERCENT / 100);
  const received = Math.max(0, a - stripeFee - centaurosFee);

  const fmt = (n) =>
    new Intl.NumberFormat(locale || "es", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

  const rows = [
    { key: "invoice", label: t("pricing.invoiceAmount"), value: fmt(a), Icon: ReceiptText, tone: "neutral" },
    { key: "stripe", label: t("pricing.stripeFee"), value: `− ${fmt(stripeFee)}`, isStripe: true, tone: "minus" },
    { key: "centauros", label: t("pricing.centaurosFee"), value: `− ${fmt(centaurosFee)}`, Icon: Sparkles, tone: "minus" },
    { key: "received", label: t("pricing.received"), value: fmt(received), Icon: Wallet, tone: "final" },
  ];

  return (
    <section id="pricing" className="scroll-mt-24 bg-white py-28 md:py-36">
      <Container>
        <div className="max-w-3xl">
          <SectionLabel>{t("pricing.label")}</SectionLabel>
          <Reveal>
            <h2 className="text-3xl font-semibold leading-tight tracking-tight text-black sm:text-4xl lg:text-5xl">
              {t("pricing.title")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-lg leading-relaxed text-black/55">{t("pricing.subtitle")}</p>
          </Reveal>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-4 lg:grid-cols-[0.9fr_1.1fr] lg:gap-6">
          {/* Left — transparent explanation */}
          <div className="flex flex-col gap-4">
            <Reveal>
              <div className="h-full rounded-3xl border border-black/[0.07] bg-white p-8">
                <div className="flex items-center gap-3">
                  <StripeMark />
                </div>
                <h3 className="mt-5 text-xl font-semibold tracking-tight text-black">{t("pricing.stripeTitle")}</h3>
                <p className="mt-2 text-base leading-relaxed text-black/55">{t("pricing.stripeDesc")}</p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-black/[0.04] px-4 py-2 text-sm font-semibold text-black">
                  {STRIPE_FEE.percent}% + {fmt(STRIPE_FEE.fixed)}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.08}>
              <div className="h-full rounded-3xl border border-green/20 bg-[#EAF7F0] p-8">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl brand-gradient text-white">
                  <Sparkles strokeWidth={1.75} className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-xl font-semibold tracking-tight text-black">{t("pricing.centaurosTitle")}</h3>
                <p className="mt-2 text-base leading-relaxed text-black/60">{t("pricing.centaurosDesc")}</p>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#08AC7D]">
                  +{CENTAUROS_PERCENT}%
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right — live calculator */}
          <Reveal delay={0.12}>
            <div className="rounded-3xl border border-black/[0.07] bg-white p-7 shadow-[0_40px_90px_-40px_rgba(0,0,0,0.3)] md:p-9" data-testid="pricing-calculator">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium uppercase tracking-[0.15em] text-black/40">{t("pricing.calcTitle")}</span>
              </div>

              {/* amount input */}
              <div className="mt-5 flex items-center justify-between rounded-2xl border border-black/[0.08] bg-black/[0.02] px-5 py-4">
                <label htmlFor="pricing-amount" className="text-sm text-black/50">{t("pricing.invoiceAmount")}</label>
                <div className="flex items-center gap-1">
                  <input
                    id="pricing-amount"
                    data-testid="pricing-amount-input"
                    type="number"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value === "" ? 0 : Number(e.target.value))}
                    className="w-28 bg-transparent text-right text-2xl font-bold tracking-tight text-black outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <span className="text-2xl font-bold text-black">€</span>
                </div>
              </div>
              <input
                type="range"
                min="10"
                max="2000"
                step="10"
                value={Math.min(2000, Math.max(10, a))}
                onChange={(e) => setAmount(Number(e.target.value))}
                data-testid="pricing-amount-slider"
                className="mt-4 w-full accent-[#08AC7D]"
                aria-label={t("pricing.invoiceAmount")}
              />

              {/* flow */}
              <div className="mt-6 space-y-2.5">
                {rows.map((r, i) => (
                  <div key={r.key}>
                    <div
                      data-testid={`pricing-row-${r.key}`}
                      className={`flex items-center justify-between rounded-2xl px-5 py-4 ${
                        r.tone === "final"
                          ? "brand-gradient text-white"
                          : "border border-black/[0.07] bg-white"
                      }`}
                    >
                      <span className={`flex items-center gap-2.5 text-sm font-medium ${r.tone === "final" ? "text-white/90" : "text-black/55"}`}>
                        {r.isStripe ? (
                          <StripeMark />
                        ) : (
                          <span className={`flex h-8 w-8 items-center justify-center rounded-full ${r.tone === "final" ? "bg-white/20 text-white" : "bg-black/[0.05] text-black/60"}`}>
                            <r.Icon className="h-4 w-4" strokeWidth={2} />
                          </span>
                        )}
                        {r.label}
                      </span>
                      <motion.span
                        key={r.value}
                        initial={{ opacity: 0.4, y: -3 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.25 }}
                        className={`tabular-nums font-bold tracking-tight ${
                          r.tone === "final" ? "text-2xl text-white" : r.tone === "minus" ? "text-base text-red-500" : "text-lg text-black"
                        }`}
                      >
                        {r.value}
                      </motion.span>
                    </div>
                    {i < rows.length - 1 && (
                      <div className="flex justify-center py-0.5 text-black/20">
                        <ArrowDown className="h-4 w-4" strokeWidth={2} />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <p className="mt-6 text-xs leading-relaxed text-black/40">{t("pricing.note")}</p>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
